import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  createUrlTreeFromSnapshot,
  UrlTree,
} from '@angular/router';
import { ConfirmationMessageModalComponent } from '@components/modals/confirmation-message-modal/confirmation-message-modal.component';
import { CULTURE_ROUTE_PARAM } from '@constants/app.constants';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { UserService } from '@services/api-clients/user.service';
import { ModalService } from '@services/modal/modal.service';
import { catchError, first, map, Observable, of } from 'rxjs';

export const resetPasswordGuard = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const userService = inject(UserService);
  const modalService = inject(ModalService);
  const store = inject(Store);
  const token = route.queryParams['token'] as string;
  const culture = route.params[CULTURE_ROUTE_PARAM] as string;

  try {
    if (!token) {
      return of(createUrlTreeFromSnapshot(route, [`/${culture}`]));
    }

    return userService.validateResetPasswordToken(token).pipe(
      first((response) => !!response),
      concatLatestFrom(() => store.select(selectAppCmsCopy)),
      map(([response, appCopy]) => {
        if (response.data) {
          return true;
        }

        modalService.show(ConfirmationMessageModalComponent, {
          initialState: {
            messageText: appCopy?.resetTokenExpiredCopy,
            titleText: appCopy?.resetTokenExpiredTitle,
          },
        });

        return createUrlTreeFromSnapshot(route, [`/${culture}`]);
      }),
      catchError(() => of(false)),
    );
  } catch {
    return of(false);
  }
};
